import { useEffect, useRef, useState } from "react";
import { jsPDF } from "jspdf";
import { useNavigate, useParams } from "react-router-dom";
import { api, errMsg, PRIORITIES, PRIORITY_STYLES, fmtDate, fileUrl } from "@/lib/api";
import ImageThumb from "@/components/ImageThumb";
import FinancialCard from "@/components/FinancialCard";
import { useStatuses } from "@/lib/useStatuses";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Send, Phone, User, MessageCircle, Clock, CheckCheck, AlertTriangle, Trash2, History, Lock, Megaphone, Paperclip, Camera, X, FileText, Activity, Flag, ClipboardList, Layers, Tags, Building2, Pencil, Printer, Download } from "lucide-react";

const WA_STATUS = { sent: "Enviado", delivered: "Entregue", read: "Lido", failed: "Falhou", error: "Erro", simulated: "Simulado" };

function Field({ label, value, onChange, items, testId, none }) {
  return (
    <div>
      <p className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wider text-slate-500 mb-1.5">{label}</p>
      <Select value={value ?? "none"} onValueChange={(v) => onChange(v === "none" ? null : v)}>
        <SelectTrigger data-testid={testId}><SelectValue /></SelectTrigger>
        <SelectContent className="bg-white dark:bg-slate-900">
          {none && <SelectItem value="none">{none}</SelectItem>}
          {items.map((s) => <SelectItem key={s.id || s} value={s.id || s}>{s.name || s}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

export default function OrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPerm, user } = useAuth();
  const { active: statusList, styleOf, byName } = useStatuses();
  const [order, setOrder] = useState(null);
  const [cat, setCat] = useState({ sectors: [], queues: [], types: [], users: [] });
  const [messages, setMessages] = useState([]);
  const [audit, setAudit] = useState([]);
  const [reply, setReply] = useState("");
  const [internal, setInternal] = useState(false);
  const [sending, setSending] = useState(false);
  const [updOpen, setUpdOpen] = useState(false);
  const [upd, setUpd] = useState({ body: "", send_whatsapp: true });
  const chatRef = useRef(null);
  const [files, setFiles] = useState([]);
  const fileRef = useRef(null);
  const camRef = useRef(null);

  const load = async () => {
    const [o, m, a] = await Promise.all([api.get(`/orders/${id}`), api.get(`/orders/${id}/messages`), api.get(`/orders/${id}/audit`)]);
    setOrder(o.data); setMessages(m.data); setAudit(a.data);
  };

  useEffect(() => {
    load();
    const t = setInterval(() => { if (document.visibilityState === "visible") load().catch(() => {}); }, 5000);
    return () => clearInterval(t);
  }, [id]); // eslint-disable-line

  useEffect(() => {
    Promise.all([api.get("/sectors"), api.get("/queues"), api.get("/os-types"), api.get("/users")])
      .then(([s, q, t, u]) => setCat({ sectors: s.data, queues: q.data, types: t.data, users: u.data }));
  }, [id]); // eslint-disable-line

  useEffect(() => { chatRef.current?.scrollTo({ top: chatRef.current.scrollHeight, behavior: "smooth" }); }, [messages]);

  const updateField = async (field, value) => {
    try { await api.put(`/orders/${id}`, { [field]: value }); toast.success("Atualizado"); load(); }
    catch (e) { toast.error(errMsg(e, "Erro ao atualizar")); }
  };

  const sendReply = async (body, sendWa, isInternal, useFiles = false) => {
    const list = useFiles ? files : [];
    if (!body.trim() && !list.length) return;
    setSending(true);
    try {
      const ids = [];
      for (const f of list) {
        const fd = new FormData();
        fd.append("file", f);
        const up = await api.post(`/orders/${id}/attachments`, fd);
        ids.push(up.data.id);
      }
      await api.post(`/orders/${id}/messages`, { body, attachment_ids: ids, direction: "outbound", send_whatsapp: sendWa, internal: isInternal });
      toast.success(isInternal ? "Observação interna registrada" : sendWa ? "Atualização enviada via WhatsApp" : "Registrado na OS");
      if (useFiles) setFiles([]);
      setReply(""); setUpd({ body: "", send_whatsapp: true }); setUpdOpen(false); load();
    } catch (e) { toast.error(errMsg(e, "Erro ao enviar")); } finally { setSending(false); }
  };

  const pick = (e) => {
    const list = Array.from(e.target.files || []);
    e.target.value = "";
    addFiles(list);
  };

  const pastePick = (e) => {
    const imgs = Array.from(e.clipboardData?.files || []).filter((f) => f.type.startsWith("image/"));
    if (!imgs.length) return;
    e.preventDefault();
    const stamp = new Date().toISOString().slice(0, 19).replace(/[-:T]/g, "");
    addFiles(imgs.map((f, i) => new File([f], `print-${stamp}-${i + 1}.${(f.type.split("/")[1] || "png").replace("jpeg", "jpg")}`, { type: f.type })));
  };

  const addFiles = (list) => {
    const ok = [];
    for (const f of list) {
      if (f.size > 15 * 1024 * 1024) { toast.error(`${f.name}: excede 15 MB`); continue; }
      ok.push(f);
    }
    setFiles((cur) => {
      if (cur.length + ok.length > 5) toast.error("Máximo de 5 arquivos por envio");
      return [...cur, ...ok].slice(0, 5);
    });
  };

  const printableMessages = () => messages.filter((m) =>
    !m.internal && m.author !== "Sistema"
  );

  const printOrder = () => {
    const esc = (value) => String(value ?? "").replace(/[&<>"']/g, (c) => ({
      "&": "&", "<": "<", ">": ">", '"': "&quot;", "'": "&#39;"
    }[c]));
    const rows = [
      ["Cliente", order.customer_name],
      ["Status", order.status],
      ["Prioridade", order.priority],
      ["Fila", order.queue_name],
      ["Responsável", order.assigned_name],
      ["Tipo de OS", order.os_type_name],
      ["Setor", order.sector_name],
      ["Aberta em", order.created_at ? new Date(order.created_at).toLocaleString("pt-BR") : "—"],
      ["Encerrada em", order.closed_at ? new Date(order.closed_at).toLocaleString("pt-BR") : "—"],
    ].filter(([, value]) => value);
    const details = rows.map(([label, value]) =>
      `<div class="item"><b>${esc(label)}</b><span>${esc(value)}</span></div>`
    ).join("");
    const messageHtml = printableMessages().map((m) => {
      const date = m.created_at ? new Date(m.created_at).toLocaleString("pt-BR") : "";
      const attachments = (m.attachments || []).map((a) => {
        const url = esc(fileUrl(a.url));
        return a.mimetype?.startsWith("image/")
          ? `<div class="attachment"><img src="${url}" alt="${esc(a.filename || "Imagem anexada")}"><small>${esc(a.filename || "Imagem")}</small></div>`
          : `<div class="attachment">Anexo: ${esc(a.filename || "arquivo")}</div>`;
      }).join("");
      return `<article class="message"><div class="meta">${esc(m.author || "")} · ${esc(date)}</div><div class="body">${esc(m.body || "")}</div>${attachments}</article>`;
    }).join("");

    const popup = window.open("", "_blank");
    if (!popup) {
      toast.error("Permita a abertura de pop-ups para imprimir.");
      return;
    }
    popup.document.open();
    popup.document.write(`<!doctype html><html lang="pt-BR"><head><meta charset="utf-8">
      <title>OS #${esc(order.number)} — ${esc(order.title)}</title>
      <style>
        body{font:13px Arial,sans-serif;line-height:1.5;color:#1e293b;margin:32px auto;max-width:820px}
        h1{font-size:21px;line-height:1.25;margin:0 0 16px}.grid{display:grid;grid-template-columns:1fr 1fr;gap:8px}
        .item{border:1px solid #dbe2ea;border-radius:6px;padding:8px}
        .item b{display:block;color:#64748b;font-size:11px;text-transform:uppercase;margin-bottom:4px}
        h2{font-size:15px;margin:20px 0 9px;border-bottom:1px solid #cbd5e1;padding-bottom:7px}
        .message{padding:10px 0;border-bottom:1px solid #e2e8f0;break-inside:avoid}
        .meta{font-size:10px;color:#64748b;margin-bottom:4px}.body{white-space:pre-wrap;overflow-wrap:anywhere}
        .attachment{margin-top:8px}.attachment img{display:block;max-width:100%;max-height:500px;object-fit:contain}
        .attachment small{color:#64748b}
        @media print{body{margin:15mm}.message,.item{break-inside:avoid}}
      </style></head><body>
      <h1>Ordem de Serviço #${esc(order.number)} — ${esc(order.title)}</h1>
      <div class="grid">${details}</div><h2>Mensagens</h2>
      ${messageHtml || "<p>Nenhuma mensagem para exibir.</p>"}
      <script>window.onload=()=>{window.focus();window.print();};<\/script>
      </body></html>`);
    popup.document.close();
  };

  const exportPdf = async () => {
    try {
      const doc = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
      const margin = 15;
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const contentWidth = pageWidth - margin * 2;
      let y = margin;

      const addPageIfNeeded = (height = 7) => {
        if (y + height > pageHeight - margin) {
          doc.addPage();
          y = margin;
        }
      };

      const writeText = (value, options = {}) => {
        const {
          size = 10,
          bold = false,
          color = [30, 41, 59],
          indent = 0,
          gapAfter = 2,
        } = options;
        doc.setFont("helvetica", bold ? "bold" : "normal");
        doc.setFontSize(size);
        doc.setTextColor(...color);
        const lines = doc.splitTextToSize(String(value ?? ""), contentWidth - indent);
        const lineHeight = size * 0.42;
        addPageIfNeeded(lines.length * lineHeight + gapAfter);
        doc.text(lines, margin + indent, y);
        y += lines.length * lineHeight + gapAfter;
      };

      const fields = [
        ["Cliente", order.customer_name],
        ["Status", order.status],
        ["Prioridade", order.priority],
        ["Fila", order.queue_name],
        ["Responsável", order.assigned_name],
        ["Tipo de OS", order.os_type_name],
        ["Setor", order.sector_name],
        ["Aberta em", order.created_at ? new Date(order.created_at).toLocaleString("pt-BR") : "—"],
        ["Encerrada em", order.closed_at ? new Date(order.closed_at).toLocaleString("pt-BR") : "—"],
      ].filter(([, value]) => value);

      writeText(`Ordem de Serviço #${order.number} — ${order.title || ""}`, {
        size: 18, bold: true, gapAfter: 6,
      });

      for (const [label, value] of fields) {
        writeText(`${label}: ${value}`, { size: 10, gapAfter: 2 });
      }

      y += 3;
      writeText("Mensagens", {
        size: 14, bold: true, gapAfter: 4,
        color: [30, 41, 59],
      });

      const visibleMessages = printableMessages().filter((m) => {
        const body = String(m.body || "").trim();
        const hasAttachments = Array.isArray(m.attachments) && m.attachments.length > 0;
        // Evita imprimir o aviso automático corrompido; os arquivos continuam listados abaixo.
        return !(hasAttachments && /^(?:Ø=ÜÎ|O=ÜÎ)?\s*�?\s*Anexo\s*$/i.test(body));
      });

      for (const m of visibleMessages) {
        const date = m.created_at ? new Date(m.created_at).toLocaleString("pt-BR") : "";
        const meta = [m.author, date].filter(Boolean).join(" · ");

        addPageIfNeeded(12);
        y += 2;
        writeText(meta, { size: 9, bold: true, color: [100, 116, 139], gapAfter: 3 });

        if (m.body) {
          writeText(m.body, { size: 10, gapAfter: 2 });
        }

        for (const a of (m.attachments || [])) {
          const name = a.filename || "arquivo";
          if (a.mimetype?.startsWith("image/") && a.url) {
            try {
              const response = await fetch(fileUrl(a.url));
              if (!response.ok) throw new Error(`HTTP ${response.status}`);
              const blob = await response.blob();
              const dataUrl = await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result);
                reader.onerror = reject;
                reader.readAsDataURL(blob);
              });
              const format = blob.type.includes("png") ? "PNG" : "JPEG";
              const props = doc.getImageProperties(dataUrl);
              const maxWidth = contentWidth - 5;
              const maxHeight = 75;
              const ratio = Math.min(maxWidth / props.width, maxHeight / props.height, 1);
              const imageWidth = props.width * ratio;
              const imageHeight = props.height * ratio;
              addPageIfNeeded(imageHeight + 12);
              doc.addImage(dataUrl, format, margin + 3, y, imageWidth, imageHeight);
              y += imageHeight + 2;
              writeText(name, { size: 8, color: [100, 116, 139], gapAfter: 3 });
            } catch (error) {
              console.warn("Não foi possível incluir a imagem no PDF:", name, error);
              writeText(`Imagem anexada: ${name}`, { size: 9, color: [100, 116, 139] });
            }
          } else {
            writeText(`Anexo: ${name}`, { size: 9, color: [100, 116, 139] });
          }
        }

        doc.setDrawColor(226, 232, 240);
        addPageIfNeeded(3);
        doc.line(margin, y, pageWidth - margin, y);
        y += 4;
      }

      if (!visibleMessages.length) {
        writeText("Nenhuma mensagem para exibir.", { size: 10 });
      }

      doc.save(`OS-${String(order.number || id).replace(/[^a-zA-Z0-9_-]/g, "_")}.pdf`);
    } catch (error) {
      console.error("Erro ao exportar PDF da OS:", error);
      toast.error("Não foi possível gerar o PDF desta OS.");
    }
  };

  const del = async () => {
    if (!window.confirm(`Excluir OS #${order.number}? Esta ação não pode ser desfeita.`)) return;
    await api.delete(`/orders/${id}`); toast.success("OS excluída"); navigate("/orders");
  };

  if (!order) return <div className="p-4 sm:p-8 text-slate-500">Carregando...</div>;
  const locked = !!order.assigned_to && order.assigned_to !== user?.id && !["SUPER_ADMIN", "TENANT_ADMIN", "SUPERVISOR"].includes(user?.role);
  const canStatus = hasPerm("change_status") && !locked, canAssign = hasPerm("assign_os") && !locked, canEdit = hasPerm("edit_os") && !locked;
  const statusOptions = statusList.some((s) => s.name === order.status) ? statusList : [{ id: order.status, name: order.status, inactive: true }, ...statusList];
  const isFinal = ["resolved", "cancelled"].includes(byName[order.status]?.kind);
  const keep = (list, id, ok) => list.filter((x) => ok(x) || x.id === id);
  const act = { queues: keep(cat.queues, order.queue_id, (q) => q.status !== "Inativa"), sectors: keep(cat.sectors, order.sector_id, (x) => x.active !== false), types: keep(cat.types, order.os_type_id, (x) => x.active !== false) };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5 sm:space-y-6">
      <Card className="border-slate-200 dark:border-slate-800 p-4 sm:p-5">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-center">
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-2">
            <Button variant="ghost" size="sm" onClick={() => navigate("/orders")} data-testid="back-btn">
              <ArrowLeft className="w-4 h-4 mr-1" /> Voltar
            </Button>
            <h1 className="min-w-0 text-xl sm:text-2xl font-extrabold text-slate-900 dark:text-white tracking-tight" style={{ fontFamily: "Plus Jakarta Sans, sans-serif" }}>
              OS #{order.number} <span className="mx-1">—</span> {order.title}
            </h1>
            <div className="flex w-full flex-wrap items-center gap-x-4 gap-y-1 pl-2 text-xs text-slate-500 dark:text-slate-400">
              <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />Aberta em {new Date(order.created_at).toLocaleString("pt-BR")}</span>
              {order.closed_at && <span className="flex items-center gap-1"><CheckCheck className="h-3.5 w-3.5 text-emerald-600" />Encerrada em {new Date(order.closed_at).toLocaleString("pt-BR")}</span>}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={exportPdf}><Download className="mr-1.5 h-4 w-4" />Exportar PDF</Button>
            <Button variant="outline" size="sm" disabled={!canEdit} onClick={async () => {
              const title = window.prompt("Novo assunto da OS:", order.title || "");
              if (title === null || !title.trim() || title.trim() === order.title) return;
              await updateField("title", title.trim());
            }}><Pencil className="mr-1.5 h-4 w-4" />Editar OS</Button>
            <Button variant="outline" size="sm" onClick={printOrder}><Printer className="mr-1.5 h-4 w-4" />Imprimir</Button>
            {hasPerm("delete_os") && <Button variant="outline" size="sm" className="text-rose-600" onClick={del} data-testid="delete-os-btn"><Trash2 className="mr-1.5 h-4 w-4" />Excluir</Button>}
          </div>
        </div>
      </Card>

      {locked && (
        <div className="flex items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800 px-4 py-3 text-sm text-amber-800 dark:text-amber-200" data-testid="locked-banner">
          <Lock className="w-4 h-4" /> Esta OS está sob responsabilidade de <b>{order.assigned_name}</b>. Somente o responsável pode dar sequência ao atendimento.
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 xl:gap-5 items-start">
        <Card className="lg:col-span-8 xl:col-span-9 border-slate-200 dark:border-slate-800 overflow-hidden flex flex-col h-[68dvh] min-h-[420px] max-h-[680px] lg:h-[calc(100dvh-190px)] lg:min-h-[500px]" data-testid="chat-panel">
          <div className="px-5 py-4 bg-emerald-600 text-white flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center font-bold">{order.customer_name[0]?.toUpperCase()}</div>
            <div className="flex-1"><p className="font-semibold">{order.customer_name}</p><p className="text-xs text-emerald-50 flex items-center gap-1"><Phone className="w-3 h-3" />{order.customer_phone}</p></div>
            <div className="flex items-center gap-2">
              {hasPerm("reply_customer") && !locked && (
                <Button onClick={() => setUpdOpen(true)} variant="secondary" size="sm" className="bg-white/15 text-white hover:bg-white/25 border-0" data-testid="send-update-btn">
                  <Megaphone className="w-4 h-4 mr-1.5" /> Atualização
                </Button>
              )}
              <MessageCircle className="w-5 h-5 opacity-80" />
            </div>
          </div>
          <div ref={chatRef} className="flex-1 overflow-y-auto overscroll-contain p-4 sm:p-5 space-y-3 min-h-0" style={{ background: "linear-gradient(180deg, #E5DDD5 0%, #E8E0D7 100%)" }}>
            {messages.map((m) => {
              const out = m.direction === "outbound";
              return (
                <div key={m.id} className={`flex ${out ? "justify-end" : "justify-start"}`}>
                  <div className={`${m.internal ? "bg-amber-100 text-amber-900 border border-amber-300" : out ? "bg-emerald-500 text-white" : "bg-white text-slate-900"} rounded-2xl ${out ? "rounded-tr-sm" : "rounded-tl-sm"} px-4 py-2.5 shadow-sm max-w-[85%] sm:max-w-[75%]`} data-testid={`msg-${m.id}`}>
                    <p className={`text-xs font-semibold mb-0.5 ${out ? (m.internal ? "text-amber-700" : "text-emerald-100") : "text-emerald-700"}`}>
                      {m.internal && <Lock className="w-3 h-3 inline mr-1" />}{m.author}{m.internal && " · observação interna"}
                    </p>
                    <p className="text-sm whitespace-pre-wrap">{m.body}</p>
                    {m.attachments?.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2" data-testid={`attachments-${m.id}`}>
                        {m.attachments.map((a) => a.mimetype?.startsWith("image/") ? (
                          <ImageThumb key={a.id} attachment={a} />
                        ) : a.mimetype?.startsWith("audio/") ? (
                          <audio key={a.id} controls preload="none" src={fileUrl(a.url)} className="max-w-full h-10" data-testid={`audio-${a.id}`} />
                        ) : (
                          <a key={a.id} href={fileUrl(a.url)} target="_blank" rel="noreferrer" className="text-xs underline flex items-center gap-1"><Paperclip className="w-3 h-3" />{a.filename}{a.size ? ` (${Math.max(1, Math.round(a.size / 1024))} KB)` : ""}</a>
                        ))}
                      </div>
                    )}
                    <p className={`text-[10px] mt-1 flex items-center gap-1 justify-end ${out && !m.internal ? "text-emerald-100" : "text-slate-500"}`}>
                      {new Date(m.created_at).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                      {out && !m.internal && <>{m.wa_status && <span>· {WA_STATUS[m.wa_status] || m.wa_status}</span>}<CheckCheck className="w-3 h-3" /></>}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
          {hasPerm("reply_customer", "edit_os") && (
            <div className="p-4 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900">
              <div className="flex flex-col gap-2">
                <Textarea value={reply} onChange={(e) => setReply(e.target.value)} placeholder={internal ? "Observação interna (não enviada ao cliente)..." : "Digite sua resposta..."} rows={2} className="resize-none" data-testid="reply-input"
                  onPaste={pastePick}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendReply(reply, !internal, internal, true); } }} />
                <div className="flex items-center gap-2">
                  <input ref={fileRef} type="file" multiple hidden onChange={pick} accept=".jpg,.jpeg,.png,.webp,.pdf,.doc,.docx,.xls,.xlsx,image/*" data-testid="file-input" />
                  <input ref={camRef} type="file" hidden onChange={pick} accept="image/*" capture="environment" data-testid="camera-input" />
                  <Button type="button" variant="outline" size="icon" className="h-10 w-10 shrink-0" disabled={sending} onClick={() => camRef.current?.click()} title="Tirar foto" aria-label="Tirar foto" data-testid="camera-btn"><Camera className="w-4 h-4" /></Button>
                  <Button type="button" variant="outline" size="icon" className="h-10 w-10 shrink-0" disabled={sending} onClick={() => fileRef.current?.click()} title="Anexar arquivo" aria-label="Anexar arquivo" data-testid="attach-btn"><Paperclip className="w-4 h-4" /></Button>
                  <div className="flex-1" />
                  <label title={internal ? "Nota interna — não será enviada ao cliente" : "Mensagem ao cliente — será enviada via WhatsApp"} className={`inline-flex h-10 cursor-pointer items-center justify-center gap-2 rounded-md border px-3 text-sm font-medium transition-colors ${internal ? "border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200" : "border-slate-200 bg-white text-slate-700 hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"}`}>
                    <input type="checkbox" className="sr-only" checked={internal} onChange={(e) => setInternal(e.target.checked)} data-testid="internal-note-checkbox" />
                    <Lock className="h-4 w-4" />
                    <span>{internal ? "Nota interna" : "Mensagem ao cliente"}</span>
                  </label>
                  <Button onClick={() => sendReply(reply, !internal, internal, true)} disabled={sending || (!reply.trim() && !files.length)} className="h-10 shrink-0 bg-emerald-600 px-3 sm:px-4 hover:bg-emerald-700" aria-label="Enviar mensagem" data-testid="send-reply-btn"><Send className="w-4 h-4 sm:mr-2" /><span>Enviar</span></Button>
                </div>
              </div>
              {files.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2" data-testid="pending-files">
                  {files.map((f, i) => (
                    <span key={i} className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 pl-2.5 pr-1 py-1 text-xs max-w-full">
                      <FileText className="w-3 h-3 shrink-0" />
                      <span className="truncate max-w-[180px]">{f.name}</span>
                      <span className="text-slate-400">{f.size >= 1048576 ? `${(f.size / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(f.size / 1024))} KB`}</span>
                      <button type="button" onClick={() => setFiles((cur) => cur.filter((_, j) => j !== i))} className="rounded-full p-0.5 hover:bg-slate-200 dark:hover:bg-slate-700" aria-label="Remover"><X className="w-3 h-3" /></button>
                    </span>
                  ))}
                </div>
              )}

            </div>
          )}
        </Card>

        <div className="lg:col-span-4 xl:col-span-3 space-y-3 lg:max-h-[calc(100dvh-190px)] lg:overflow-y-auto lg:pr-1">
          <Card className="p-4 border-slate-200 dark:border-slate-800 space-y-3">
            <div>
              <h4 className="flex items-center gap-2 font-bold text-slate-900 dark:text-white mb-2">
                <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-emerald-600 text-white">
                  <Activity className="h-4 w-4" />
                </span>
                Status
              </h4>
              <Select value={order.status} onValueChange={(v) => updateField("status", v)} disabled={!canStatus}>
                <SelectTrigger data-testid="status-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white dark:bg-slate-900">{statusOptions.map((s) => <SelectItem key={s.name} value={s.name}><span className="inline-flex items-center gap-2"><span className="w-2 h-2 rounded-full" style={{ background: s.color || "#64748B" }} />{s.name}{s.inactive ? " (inativo)" : ""}</span></SelectItem>)}</SelectContent>
              </Select>
              <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">A alteração de status notifica o cliente.</p>
            </div>
          </Card>

          <Card className="p-4 border-slate-200 dark:border-slate-800 space-y-3">
            <div>
              <h4 className="flex items-center gap-2 font-bold text-slate-900 dark:text-white mb-2">
                <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-amber-500 text-white">
                  <Flag className="h-4 w-4" />
                </span>
                Prioridade
              </h4>
              <Select value={order.priority} onValueChange={(v) => updateField("priority", v)} disabled={!canEdit}>
                <SelectTrigger data-testid="priority-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white dark:bg-slate-900">{PRIORITIES.map((p) => {
                  const dotColor = {
                    Baixa: "bg-slate-500",
                    Normal: "bg-sky-500",
                    Alta: "bg-orange-500",
                    Urgente: "bg-rose-500",
                  }[p] || "bg-slate-400";
                  return (
                    <SelectItem key={p} value={p}>
                      <span className="inline-flex items-center gap-2">
                        <span className={`h-2 w-2 rounded-full ${dotColor}`} />
                        {p}
                      </span>
                    </SelectItem>
                  );
                })}</SelectContent>
              </Select>
              <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">SLA até {fmtDate(order.sla_due_at)}</p>
            </div>
          </Card>

          <Card className="p-4 border-slate-200 dark:border-slate-800 space-y-3">
            <h4 className="flex items-center gap-2 font-bold text-slate-900 dark:text-white"><ClipboardList className="h-4 w-4 text-emerald-600" />Informações da OS</h4>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-3 gap-y-3">
              {canAssign ? <Field label={<><Layers className="h-3.5 w-3.5" />Fila</>} value={order.queue_id} onChange={(v) => updateField("queue_id", v)} items={act.queues} testId="queue-select" none="Sem fila" />
                : <div><p className="flex items-center gap-1.5 text-xs uppercase text-slate-500"><Layers className="h-3.5 w-3.5" />Fila</p><p className="text-sm">{order.queue_name || "—"}</p></div>}
              {canAssign ? <Field label={<><User className="h-3.5 w-3.5" />Responsável</>} value={order.assigned_to} onChange={(v) => updateField("assigned_to", v)} items={(cat.users || []).filter((u) => u.role !== "CONSULTA")} testId="assignee-select" none="Não atribuída" />
                : <div><p className="flex items-center gap-1.5 text-xs uppercase text-slate-500"><User className="h-3.5 w-3.5" />Responsável</p><p className="text-sm">{order.assigned_name || "Não atribuída"}</p></div>}
              {canEdit && <Field label={<><Tags className="h-3.5 w-3.5" />Tipo de OS</>} value={order.os_type_id} onChange={(v) => updateField("os_type_id", v)} items={act.types} testId="type-select" none="Sem tipo" />}
              {canEdit && <Field label={<><Building2 className="h-3.5 w-3.5" />Setor</>} value={order.sector_id} onChange={(v) => updateField("sector_id", v)} items={act.sectors} testId="sector-select" none="Sem setor" />}
            </div>
          </Card>



          <FinancialCard orderId={id} />

          <Card className="p-5 border-slate-200 dark:border-slate-800" data-testid="audit-panel">
            <h4 className="font-bold text-slate-900 dark:text-white flex items-center gap-2 mb-3"><History className="w-4 h-4" /> Histórico</h4>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {audit.map((a) => (
                <div key={a.id} className="text-xs border-l-2 border-slate-200 pl-2">
                  <p className="text-slate-700 dark:text-slate-300"><span className="font-semibold">{a.user_name}</span> · {a.action === "financial" ? "valores financeiros alterados" : a.action}{a.detail?.status ? ` → ${a.detail.status}` : ""}{a.detail?.direction ? ` (${a.detail.direction})` : ""}</p>
                  <p className="text-slate-400">{fmtDate(a.created_at)}</p>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>

      <Dialog open={updOpen} onOpenChange={setUpdOpen}>
        <DialogContent className="bg-white dark:bg-slate-900">
          <DialogHeader><DialogTitle>Enviar atualização — OS #{order.number}</DialogTitle></DialogHeader>
          <Textarea rows={5} value={upd.body} onChange={(e) => setUpd({ ...upd, body: e.target.value })} placeholder="Mensagem para o cliente..." data-testid="update-body-input" />
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" className="accent-emerald-600" checked={upd.send_whatsapp} onChange={(e) => setUpd({ ...upd, send_whatsapp: e.target.checked })} data-testid="update-send-wa-checkbox" /> Enviar pelo WhatsApp
          </label>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUpdOpen(false)}>Cancelar</Button>
            <Button onClick={() => sendReply(upd.body, upd.send_whatsapp, false)} disabled={sending || !upd.body.trim()} className="bg-emerald-600 hover:bg-emerald-700" data-testid="update-submit-btn">ENVIAR</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
